import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PreviewSection = ({ data, onEdit }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (value) => {
    const categories = {
      'transportation': 'Transportation & Mobility',
      'energy': 'Energy & Sustainability',
      'water': 'Water Management',
      'waste': 'Waste Management',
      'housing': 'Housing & Urban Planning',
      'safety': 'Public Safety & Security',
      'governance': 'Digital Governance',
      'health': 'Healthcare & Wellness',
      'education': 'Education & Skills',
      'economy': 'Economic Development'
    };
    return categories?.[value] || value;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'bg-error text-error-foreground',
      'medium': 'bg-warning text-warning-foreground',
      'low': 'bg-success text-success-foreground'
    };
    return colors?.[priority] || 'bg-muted text-muted-foreground';
  };

  const getComplexityColor = (complexity) => {
    const colors = {
      'beginner': 'bg-success text-success-foreground',
      'intermediate': 'bg-warning text-warning-foreground',
      'advanced': 'bg-error text-error-foreground'
    };
    return colors?.[complexity] || 'bg-muted text-muted-foreground';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return 'FileText';
    if (fileType?.includes('word')) return 'FileText';
    if (fileType?.includes('powerpoint') || fileType?.includes('presentation')) return 'Presentation';
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return 'Table';
    if (fileType?.includes('image')) return 'Image';
    return 'File';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Eye" size={16} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Preview Problem Statement</h2>
            <p className="text-sm text-muted-foreground">Review how your problem will appear to students</p>
          </div>
        </div>
        
        <Button
          variant="outline"
          onClick={() => window.print()}
          iconName="Printer"
          iconPosition="left"
          iconSize={16}
        >
          Print Preview
        </Button>
      </div>
      {/* Problem Statement Card */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-primary text-primary-foreground p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold mb-2">
                {data?.title || 'Problem Title'}
              </h1>
              <div className="flex items-center space-x-4 text-primary-foreground/80">
                <span className="flex items-center space-x-1">
                  <Icon name="Building2" size={16} />
                  <span>City of Innovation</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="Calendar" size={16} />
                  <span>{formatDate(new Date()?.toISOString())}</span>
                </span>
              </div>
            </div>
            
            <div className="flex flex-col items-end space-y-2">
              {data?.priority && (
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(data?.priority)}`}>
                  {data?.priority?.charAt(0)?.toUpperCase() + data?.priority?.slice(1)} Priority
                </span>
              )}
              {data?.complexity && (
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getComplexityColor(data?.complexity)}`}>
                  {data?.complexity?.charAt(0)?.toUpperCase() + data?.complexity?.slice(1)} Level
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Problem Overview */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Problem Overview</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(1)}
                iconName="Edit"
                iconSize={14}
              >
                Edit
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <span className="text-sm font-medium text-muted-foreground">Category:</span>
                <p className="text-foreground">{getCategoryLabel(data?.category) || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Affected Population:</span>
                <p className="text-foreground">{data?.affectedPopulation || 'Not specified'}</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-muted-foreground">Description:</span>
                <p className="text-foreground mt-1 leading-relaxed">
                  {data?.description || 'No description provided'}
                </p>
              </div>
              
              {data?.background && (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Background:</span>
                  <p className="text-foreground mt-1 leading-relaxed">{data?.background}</p>
                </div>
              )}
            </div>
          </section>

          {/* Objectives & Requirements */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Objectives & Requirements</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(2)}
                iconName="Edit"
                iconSize={14}
              >
                Edit
              </Button>
            </div>
            
            {data?.objectives && data?.objectives?.length > 0 && (
              <div className="mb-4">
                <span className="text-sm font-medium text-muted-foreground">Project Objectives:</span>
                <ul className="mt-2 space-y-1">
                  {data?.objectives?.map((objective, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
                      <span className="text-foreground">{objective}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {data?.constraints && data?.constraints?.length > 0 && (
              <div className="mb-4">
                <span className="text-sm font-medium text-muted-foreground">Constraints:</span>
                <ul className="mt-2 space-y-1">
                  {data?.constraints?.map((constraint, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                      <span className="text-foreground">{constraint}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {data?.expectedDeliverables && data?.expectedDeliverables?.length > 0 && (
              <div>
                <span className="text-sm font-medium text-muted-foreground">Expected Deliverables:</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {data?.expectedDeliverables?.map((deliverable, index) => (
                    <span key={index} className="px-2 py-1 bg-muted text-muted-foreground rounded text-sm">
                      {deliverable?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Timeline */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Timeline</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(4)}
                iconName="Edit"
                iconSize={14}
              >
                Edit
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <span className="text-sm font-medium text-muted-foreground">Duration:</span>
                <p className="text-foreground">{data?.projectDuration?.replace('-', ' ') || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Start Date:</span>
                <p className="text-foreground">{formatDate(data?.projectStartDate)}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">End Date:</span>
                <p className="text-foreground">{formatDate(data?.projectEndDate)}</p>
              </div>
            </div>
            
            {data?.milestones && data?.milestones?.length > 0 && (
              <div>
                <span className="text-sm font-medium text-muted-foreground">Key Milestones:</span>
                <div className="mt-2 space-y-2">
                  {data?.milestones?.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))?.map((milestone, index) => (
                      <div key={milestone?.id} className="flex items-center space-x-3 p-2 bg-muted rounded">
                        <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <p className="font-medium text-foreground">{milestone?.title}</p>
                          <p className="text-sm text-muted-foreground">Due: {formatDate(milestone?.dueDate)}</p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </section>

          {/* Contact Information */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Contact Information</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(3)}
                iconName="Edit"
                iconSize={14}
              >
                Edit
              </Button>
            </div>
            
            {data?.primaryContact && (
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-2">Primary Contact</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium text-foreground">{data?.primaryContact?.name || 'Not specified'}</p>
                    <p className="text-sm text-muted-foreground">{data?.primaryContact?.title || 'Title not specified'}</p>
                    <p className="text-sm text-muted-foreground">{data?.primaryContact?.department || 'Department not specified'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground">{data?.primaryContact?.email || 'Email not provided'}</p>
                    <p className="text-sm text-muted-foreground">{data?.primaryContact?.phone || 'Phone not provided'}</p>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Attachments */}
          {data?.attachments && data?.attachments?.length > 0 && (
            <section>
              <h3 className="text-lg font-semibold text-foreground mb-4">Supporting Documents</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data?.attachments?.map((file) => (
                  <div key={file?.id} className="flex items-center space-x-3 p-3 border border-border rounded-lg">
                    <div className="w-8 h-8 bg-muted rounded flex items-center justify-center">
                      <Icon name={getFileIcon(file?.type)} size={16} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{file?.name}</p>
                      <p className="text-sm text-muted-foreground">{formatFileSize(file?.size)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
      {/* Preview Actions */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-accent mt-0.5" />
          <div>
            <h4 className="font-medium text-foreground mb-2">Preview Information</h4>
            <p className="text-sm text-muted-foreground mb-3">
              This is how your problem statement will appear to student teams. Make sure all information is accurate and complete before submitting.
            </p>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(1)}
                iconName="Edit"
                iconPosition="left"
                iconSize={14}
              >
                Make Changes
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.print()}
                iconName="Download"
                iconPosition="left"
                iconSize={14}
              >
                Export PDF
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewSection;